import { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, Clock, CheckCircle2, XCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLATFORM_LABEL = { youtube: "YouTube", facebook: "Facebook", instagram: "Instagram", tiktok: "TikTok" };

function statusBadge(s) {
  const map = {
    pending: { Icon: Clock, cls: "text-zinc-500", lbl: "pending" },
    running: { Icon: Loader2, cls: "text-amber-300 animate-spin", lbl: "running" },
    success: { Icon: CheckCircle2, cls: "text-emerald-400", lbl: "posted" },
    skipped: { Icon: null, cls: "text-zinc-500", lbl: "skipped" },
    failed: { Icon: XCircle, cls: "text-red-400", lbl: "failed" },
  };
  return map[s] || map.pending;
}

export default function Schedule() {
  const [data, setData] = useState(null);

  useEffect(() => {
    (async () => {
      const r = await axios.get(`${API}/schedule/upcoming?days=7`);
      setData(r.data);
    })();
  }, []);

  if (!data) return <div className="flex items-center gap-2 text-sm text-zinc-400"><Loader2 className="h-4 w-4 animate-spin" /> Loading 7-day schedule…</div>;

  const byDay = {};
  for (const r of data.rows) {
    byDay[r.day_key] = byDay[r.day_key] || [];
    byDay[r.day_key].push(r);
  }

  return (
    <div data-testid="schedule-page">
      <header className="mb-6">
        <p className="mb-1 text-xs uppercase tracking-[0.2em] text-zinc-500">Schedule</p>
        <h1 className="text-3xl font-semibold tracking-tight">Next 7 days</h1>
        <p className="mt-2 text-sm text-zinc-400">Auto-built daily at 00:30 {data.timezone}. The post runner fires every minute.</p>
      </header>
      {data.day_keys.map(dk => {
        const rows = byDay[dk] || [];
        return (
          <section key={dk} className="mb-6" data-testid={`day-${dk}`}>
            <h2 className="mb-2 text-sm font-medium text-zinc-300">{dk}</h2>
            {rows.length === 0 ? (
              <div className="rounded-md border border-dashed border-zinc-800 bg-zinc-900/20 px-4 py-3 text-xs text-zinc-500">
                Not built yet — will be generated at 00:30 {data.timezone} on this day.
              </div>
            ) : (
              <div className="overflow-x-auto rounded-lg border border-zinc-800">
                <table className="w-full text-sm">
                  <thead className="bg-zinc-900/60 text-xs uppercase tracking-wider text-zinc-500">
                    <tr>
                      <th className="px-3 py-2 text-left">Time</th>
                      <th className="px-3 py-2 text-left">Slot</th>
                      <th className="px-3 py-2 text-left">Platform</th>
                      <th className="px-3 py-2 text-left">Clip</th>
                      <th className="px-3 py-2 text-left">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, i) => {
                      const { Icon, cls, lbl } = statusBadge(r.status);
                      return (
                        <tr key={i} className="border-t border-zinc-800">
                          <td className="px-3 py-1.5 text-xs text-zinc-400 whitespace-nowrap">{r.scheduled_at_local_str?.split(" ")[1] || "?"}</td>
                          <td className="px-3 py-1.5 text-xs text-zinc-400 capitalize">{r.slot}</td>
                          <td className="px-3 py-1.5 text-xs text-zinc-200">{PLATFORM_LABEL[r.platform]}</td>
                          <td className="px-3 py-1.5 max-w-md truncate text-xs text-zinc-300" title={r.clip_title}>{r.clip_title}</td>
                          <td className="px-3 py-1.5">
                            <span className={`inline-flex items-center gap-1.5 text-xs ${cls}`}>
                              {Icon && <Icon className={`h-3.5 w-3.5 ${r.status === "running" ? "animate-spin" : ""}`} />}
                              {lbl}
                              {r.status === "success" && r.platform_url?.startsWith("http") && (
                                <a href={r.platform_url} target="_blank" rel="noreferrer" className="ml-2 underline">view</a>
                              )}
                            </span>
                            {r.status === "failed" && r.error_message && (
                              <p className="mt-0.5 max-w-md truncate text-[10px] text-red-300/80" title={r.error_message}>{r.error_message}</p>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
}
